import { apiClient } from '@/api/client';
import type {
  BookingStatus,
  ExamBookingResponse,
  ExamSection,
  ExamSectionRow,
  PaginatedExamBookings,
  UpdateExamSectionRequest,
} from '@/types/shared';

/**
 * T126 — the shared `UpdateExamSectionRequest` contract (`packages/shared/src/contracts/exams.ts`)
 * types `usdEgpRate` as a decimal-as-STRING (matching every other finance money field's wire
 * convention). The actual T123 backend input DTO (`update-exam-section.dto.ts`) deliberately
 * diverges for this ONE field: `@IsNumber({maxDecimalPlaces:6}) usdEgpRate?: number | null` — a
 * JSON number on the request body, mirroring the `SetExamPriceDto` precedent (class-validator
 * input DTOs accept a JSON number; the service formats it to a fixed-precision string before it
 * touches the DB). This local type is the FE's honest reflection of what actually goes over the
 * wire for THIS request — no shared-contract change, additive-only FE accommodation. */
export type UpdateExamSectionPayload = Omit<UpdateExamSectionRequest, 'usdEgpRate'> & {
  usdEgpRate?: number | null;
};

export interface ListExamBookingsParams {
  page?: number;
  pageSize?: number;
  /** `field:ASC` / `field:DESC` — allowed fields per exam-bookings.service.ts `applySort`:
   * `createdAt`, `studentCode`, `nameAr`, `nameEn`. */
  sort?: string;
  search?: string;
  /** E5: "student has >=1 section with this examSection" (not tied to the same row as
   * `bookingStatus` when both filters are set). */
  examSection?: ExamSection;
  /** E5: "student has >=1 section with this bookingStatus". */
  bookingStatus?: BookingStatus;
  /** T175/T168 (§20, openapi `BranchFilter`). Narrows WITHIN the caller's resolved branch scope
   * only — never widens it. Omitted by the UI entirely for a single-branch caller (T159 §5.2). */
  branch?: number;
}

/** GET /exam-bookings — Roles: Admin, Exam Coordinator, Admissions Officer, Student Affairs
 * Officer, Sales Manager. One summary row per student (all 4 sections + `bookedCount`). */
export async function listExamBookings(
  params: ListExamBookingsParams,
): Promise<PaginatedExamBookings> {
  const { data } = await apiClient.get<PaginatedExamBookings>('/exam-bookings', { params });
  return data;
}

/** GET /exam-bookings/:studentCode — the 4 sections (canonical EXAM_SECTIONS order) + bookedCount
 * + audit. 404 only if the student itself is absent (a missing section row is synthesized
 * server-side per E1, never a 404). */
export async function getExamBookings(studentCode: string): Promise<ExamBookingResponse> {
  const { data } = await apiClient.get<ExamBookingResponse>(
    `/exam-bookings/${encodeURIComponent(studentCode)}`,
  );
  return data;
}

/** PATCH /exam-bookings/:studentCode/:examSection — Roles: Admin, Exam Coordinator. Updates
 * exactly one section (the other 3 are untouched); `bookingStatus='Booked'` requires `examDate`
 * (E4 — 400 VALIDATION_ERROR otherwise, mirrored client-side as a pre-flight guard). Fields
 * omitted from the payload retain their current stored value. `examSection` is URL-encoded since
 * two of the four values contain a space (`EST I`, `EST II`). */
export async function updateExamSection(
  studentCode: string,
  examSection: ExamSection,
  payload: UpdateExamSectionPayload,
): Promise<ExamSectionRow> {
  const { data } = await apiClient.patch<ExamSectionRow>(
    `/exam-bookings/${encodeURIComponent(studentCode)}/${encodeURIComponent(examSection)}`,
    payload,
  );
  return data;
}

// --- Exam approval workflow (2026-07). Not Booked → Requested → Approved → (then Booked via PATCH).

/** POST .../request — Roles: Admin, Exam Coordinator, Sales Agent, Sales Manager. Notifies all admins. */
export async function requestExamSection(
  studentCode: string,
  examSection: ExamSection,
): Promise<ExamSectionRow> {
  const { data } = await apiClient.post<ExamSectionRow>(
    `/exam-bookings/${encodeURIComponent(studentCode)}/${encodeURIComponent(examSection)}/request`,
  );
  return data;
}

/** POST .../approve — Roles: Admin. Notifies the requester. */
export async function approveExamSection(
  studentCode: string,
  examSection: ExamSection,
): Promise<ExamSectionRow> {
  const { data } = await apiClient.post<ExamSectionRow>(
    `/exam-bookings/${encodeURIComponent(studentCode)}/${encodeURIComponent(examSection)}/approve`,
  );
  return data;
}

/** POST .../reject — Roles: Admin. Optional reason surfaced to the requester. */
export async function rejectExamSection(
  studentCode: string,
  examSection: ExamSection,
  reason?: string,
): Promise<ExamSectionRow> {
  const { data } = await apiClient.post<ExamSectionRow>(
    `/exam-bookings/${encodeURIComponent(studentCode)}/${encodeURIComponent(examSection)}/reject`,
    reason ? { reason } : {},
  );
  return data;
}
